import axios from "axios";
import { currentIntl, currentLang } from "@/i18n";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// URL canonique publique (SEO, OG, JSON-LD) : domaine de production par défaut.
export const SITE_URL = (process.env.REACT_APP_SITE_URL || "https://lechaletdebadouille.fr").replace(/\/$/, "");

export const api = axios.create({ baseURL: API });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("admin_token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  // Contenus publics : langue courante du site (fr / en / de / it)
  if ((cfg.url || "").startsWith("/public/") && !(cfg.params && cfg.params.lang)) cfg.params = { ...(cfg.params || {}), lang: currentLang() };
  return cfg;
});

export const has = (v) => v !== null && v !== undefined && v !== "" && !(Array.isArray(v) && v.length === 0);

export const errMsg = (e, fallback = "Une erreur est survenue") => {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(" ");
  return d.msg || String(d);
};

export const fmtPrice = (n) => {
  if (!has(n)) return null;
  const v = Number(n);
  const dec = Number.isInteger(v) ? 0 : 2;  // 307,50 € et non 307,5 €
  return `${v.toLocaleString(currentIntl(), { minimumFractionDigits: dec, maximumFractionDigits: 2 })} €`;
};
export const fmtDate = (s) => (s ? new Date(s).toLocaleDateString(currentIntl(), { day: "2-digit", month: "short", year: "numeric" }) : "");
export const fmtDateTime = (s) => (s ? new Date(s).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

export const POI_CATEGORIES = ["Ski", "Randonnée", "Restaurant", "Commerce", "Activité", "Patrimoine", "Village", "Vélo", "Nature", "Service"];
export const EQUIP_CATEGORIES = ["Cuisine", "Confort", "Chambres", "Salle de bain", "Extérieur", "Famille", "Ski / montagne"];
export const REQUEST_STATUSES = ["Nouveau", "À traiter", "Contacté", "Accepté hors site", "Refusé", "Archivé"];
export const CONTACT_SUBJECTS = ["Demande de séjour", "Appartement", "Séjour en groupe", "Informations pratiques", "Autre"];

// Saisonnalité : HIVER de septembre à fin mars, ÉTÉ d'avril à fin août.
const WINTER_MONTHS = [9, 10, 11, 12, 1, 2, 3];
export const seasonNow = (date = new Date()) => (WINTER_MONTHS.includes(date.getMonth() + 1) ? "winter" : "summer");

// Étiquette saison d'une photo (galerie ou fiche appartement) : "été" | "hiver" | "toutes".
export const PHOTO_SEASONS = [{ value: "toutes", label: "Toutes saisons" }, { value: "hiver", label: "Hiver" }, { value: "été", label: "Été" }];
const SEASON_TAG = { winter: "hiver", summer: "été" };
export function photoSeason(item) {
  const s = (item?.season || "").toLowerCase();
  if (s === "hiver" || s === "winter") return "hiver";
  if (s === "été" || s === "ete" || s === "summer") return "été";
  if (item?.category === "hiver") return "hiver";
  if (item?.category === "ete") return "été";
  return "toutes";
}
// Priorité d'affichage : saison en cours, puis toutes saisons, puis saison opposée (ordre d'origine conservé).
export function sortBySeason(items, season = seasonNow()) {
  const current = SEASON_TAG[season];
  const rank = (it) => { const s = photoSeason(it); return s === current ? 0 : s === "toutes" ? 1 : 2; };
  return (items || []).map((it, i) => [it, i]).sort((a, b) => rank(a[0]) - rank(b[0]) || a[1] - b[1]).map(([it]) => it);
}
// Photo principale d'un appartement selon la saison : si la photo principale est étiquetée
// pour l'autre saison et qu'une photo de la saison en cours existe, on affiche cette dernière.
export function seasonalMainImage(apt, season = seasonNow()) {
  const images = (apt?.images || []).filter((im) => has(im.url));
  const current = SEASON_TAG[season];
  const main = images.find((im) => im.url === apt?.main_image);
  if (main && photoSeason(main) !== "toutes" && photoSeason(main) !== current) {
    const alt = images.find((im) => photoSeason(im) === current) || images.find((im) => photoSeason(im) === "toutes");
    if (alt) return alt;
  }
  if (!apt?.main_image) {
    const first = sortBySeason(images, season)[0];
    if (first) return first;
  }
  return apt?.main_image ? { url: apt.main_image, alt: main?.alt } : null;
}

export function pickSeasonalImage(block) {
  const b = block || {};
  const mode = b.image_mode || "auto";
  const season = mode === "auto" ? seasonNow() : mode;
  if (season === "summer" && has(b.image_summer_url)) return b.image_summer_url;
  if (season === "winter" && has(b.image_winter_url)) return b.image_winter_url;
  return b.image_url || b.image_summer_url || b.image_winter_url || null;
}

export function fillTemplate(tpl, vars) {
  return String(tpl || "").replace(/\{(\w+)\}/g, (_, k) => (has(vars[k]) ? String(vars[k]) : ""));
}

export function mailtoLink(to, subject, body) {
  return `mailto:${to}?subject=${encodeURIComponent(subject || "")}&body=${encodeURIComponent(body || "")}`;
}
